import { useState } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Target,
  TrendingUp,
  AlertCircle,
  UserPlus,
} from 'lucide-react';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { Link } from 'react-router-dom';
import { useTrackEvent } from '../components/AnalyticsProvider';

// Question structure
interface Question {
  id: number;
  section: string;
  text: string;
  score: number;
}

// Section definitions
const SECTIONS = [
  { id: 1, title: 'Direction', subtitle: 'Clarity of Strategy', questions: [1, 2, 3, 4] },
  { id: 2, title: 'Market Reality', subtitle: 'External Awareness', questions: [5, 6, 7] },
  { id: 3, title: 'Internal Control', subtitle: 'Execution Readiness', questions: [8, 9, 10] },
  {
    id: 4,
    title: 'Execution Engine',
    subtitle: 'Keeping the work connected',
    questions: [11, 12],
  },
];

// All 12 questions
const QUESTIONS: Omit<Question, 'score'>[] = [
  {
    id: 1,
    section: 'Direction',
    text: 'We have a clearly defined long-term vision that the leadership team can articulate in one sentence.',
  },
  {
    id: 2,
    section: 'Direction',
    text: 'Our mission clearly explains who we serve, what we do, and how we create value.',
  },
  {
    id: 3,
    section: 'Direction',
    text: 'We have 3–7 defined company values that genuinely guide hiring, firing, and decision-making.',
  },
  {
    id: 4,
    section: 'Direction',
    text: 'We have written, measurable 12-month objectives across: Financial, Customer, Process, and Team.',
  },
  {
    id: 5,
    section: 'Market Reality',
    text: 'We understand our competitive landscape deeply, including pricing position and differentiation.',
  },
  {
    id: 6,
    section: 'Market Reality',
    text: 'We regularly assess external risks (economic, technological, regulatory, market shifts).',
  },
  {
    id: 7,
    section: 'Market Reality',
    text: 'We can clearly explain why customers choose us over competitors.',
  },
  {
    id: 8,
    section: 'Internal Control',
    text: 'We have identified our real strengths and weaknesses, not just surface-level assumptions.',
  },
  {
    id: 9,
    section: 'Internal Control',
    text: 'Every strategic initiative has a single accountable owner, not a group.',
  },
  {
    id: 10,
    section: 'Internal Control',
    text: 'Our objectives are broken down into specific initiatives with deadlines and measurable outcomes.',
  },
  {
    id: 11,
    section: 'Execution Engine',
    text: 'We track strategic progress monthly using a visible dashboard (red, yellow, green style).',
  },
  {
    id: 12,
    section: 'Execution Engine',
    text: 'Strategy discussions happen in a structured, recurring meeting rhythm, not just when things go wrong.',
  },
];

// Score scale labels
const SCORE_LABELS = [
  { value: 1, label: 'Not true at all' },
  { value: 2, label: 'Mostly untrue' },
  { value: 3, label: 'Partially true' },
  { value: 4, label: 'Mostly true' },
  { value: 5, label: 'Completely true' },
];

// Result bands
interface ResultBand {
  min: number;
  max: number;
  title: string;
  subtitle: string;
  color: string;
  bgColor: string;
  icon: typeof AlertCircle;
  description: string[];
}

const RESULT_BANDS: ResultBand[] = [
  {
    min: 0,
    max: 30,
    title: 'Build your strategic foundation.',
    subtitle: 'Start with direction, priorities and clear ownership.',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    icon: AlertCircle,
    description: [
      'Your answers suggest several parts of your strategic workflow need attention. Treat this as a starting point for a focused conversation.',
      'Choose one low-scoring area. Write down what good would look like, name an owner and agree on a practical next step.',
      'Begin with a shared direction and a small set of measurable objectives, then connect those priorities to the work already underway.',
    ],
  },
  {
    min: 31,
    max: 45,
    title: 'Connect the pieces of your strategy.',
    subtitle: 'Build on the structure you have and strengthen the gaps.',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    icon: TrendingUp,
    description: [
      'Your answers show a foundation to build on, alongside areas where the direction and daily work could connect more clearly.',
      'Review the questions you scored lowest. Are the gaps about strategic clarity, market understanding, ownership or your review rhythm?',
      'Link one important objective to a specific initiative, an accountable owner and a date for checking progress.',
    ],
  },
  {
    min: 46,
    max: 60,
    title: 'Keep a strong strategy moving.',
    subtitle: 'Protect what is working and sharpen the next priority.',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    icon: Target,
    description: [
      'Your answers suggest a strong strategic foundation and execution habits across the areas assessed.',
      'Use the lower-scoring questions to identify the next improvement, and discuss whether your team would score these areas the same way.',
      'Keep objectives, ownership and progress visible as conditions change. A regular review helps the plan stay useful to the decisions ahead.',
    ],
  },
];

export default function HealthCheckPage() {
  const [step, setStep] = useState(0); // 0: intro, 1-4: sections, 5: results, 6: name/email, 7: success
  const [scores, setScores] = useState<Record<number, number>>({});
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const trackEvent = useTrackEvent();

  // Calculate total score
  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
  const maxScore = 60;

  // Get current result band
  const getResultBand = (): ResultBand => {
    return (
      RESULT_BANDS.find((band) => totalScore >= band.min && totalScore <= band.max) ||
      RESULT_BANDS[0]
    );
  };

  // Get current section questions
  const getCurrentSectionQuestions = () => {
    if (step === 0 || step > 4) return [];
    const section = SECTIONS[step - 1];
    return QUESTIONS.filter((q) => section.questions.includes(q.id));
  };

  // Check if current section is complete
  const isCurrentSectionComplete = () => {
    const questions = getCurrentSectionQuestions();
    return questions.every((q) => scores[q.id] !== undefined);
  };

  // Handle score change
  const handleScoreChange = (questionId: number, score: number) => {
    setScores((prev) => ({ ...prev, [questionId]: score }));
  };

  // Handle next step
  const handleNext = () => {
    if (step === 0) {
      trackEvent({ action: 'health_check_started', category: 'health_check' });
    }
    if (step === 4) {
      trackEvent({
        action: 'health_check_completed',
        category: 'health_check',
        label: `score_${totalScore}`,
      });
    }
    setStep((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle previous step
  const handlePrev = () => {
    setStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle sign up submission - SIMPLIFIED BACKEND FLOW
  // Backend creates Clerk user, sends report email, and sends sign-in link
  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const resultBand = getResultBand();

      // Send everything to backend in ONE request
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const baseUrl = apiUrl.replace(/\/api\/?$/, '');
      const answers = QUESTIONS.map((q) => ({
        questionId: q.id,
        section: q.section,
        text: q.text,
        score: scores[q.id] ?? 0,
      })).filter((a) => a.score > 0);

      const res = await fetch(baseUrl + '/api/health-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          score: totalScore,
          resultBand: resultBand.title,
          answers,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('Health check submission failed', res.status, data);
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Success! Backend handled everything
      trackEvent({
        action: 'health_check_signup_completed',
        category: 'health_check',
        label: resultBand.title,
      });

      setIsSubmitting(false);
      setStep(7); // Show success screen
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Health check submission error:', err);
      setErrorMessage('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Intro step
  if (step === 0) {
    return (
      <MarketingLayout>
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="public-container py-3">
            <Breadcrumb items={[{ label: 'Strategy Health Check' }]} />
          </div>
        </div>
        <PageHero
          badge={<span>12 questions. A clearer next focus.</span>}
          title={
            <>
              Find the gap between
              <br />
              <span className="text-blue-600">your ambition and your execution.</span>
            </>
          }
          subtitle="Take a practical look at your direction, market awareness, ownership and review habits. Score 12 statements, see where you stand and choose where to focus next."
        >
          <button
            type="button"
            onClick={handleNext}
            className="public-button public-button--primary"
          >
            Start my health check <ArrowRight className="h-5 w-5" />
          </button>
        </PageHero>
        <section className="public-section">
          <div className="public-container">
            <div className="public-card public-card--white p-8 md:p-10 max-w-3xl mx-auto">
              <h2 className="mb-5">Make your next strategy conversation more useful.</h2>
              <p className="text-gray-600 mb-6">
                This self-assessment gives you a shared starting point. Think about how your company
                operates today, rather than how you would like it to work.
              </p>
              <ul className="space-y-4 mb-7">
                {[
                  'Check whether your direction and objectives are clear.',
                  'Reflect on the market and capabilities behind your strategic choices.',
                  'Look at ownership, deadlines and how you review progress.',
                  'Use your results to choose one practical improvement.',
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 mb-7">
                Rate each statement from 1 to 5. Your score appears after the last section; you can
                then request an emailed report and sign-in link.
              </p>
              <button onClick={handleNext} className="public-button public-button--primary">
                Start with my company’s direction <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  // Questions steps (1-4)
  if (step >= 1 && step <= 4) {
    const section = SECTIONS[step - 1];
    const questions = getCurrentSectionQuestions();
    const sectionProgress = step / 4;
    return (
      <MarketingLayout>
        <section className="public-section">
          <div className="public-container max-w-4xl">
            <div className="mb-8">
              <div
                className="h-2 bg-blue-100 rounded-full overflow-hidden"
                role="progressbar"
                aria-label="Health check sections"
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={step}
              >
                <div
                  className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
                  style={{ width: sectionProgress * 100 + '%' }}
                />
              </div>
              <p className="mt-3 text-center text-sm text-gray-600">
                Section {step} of 4 · {Object.keys(scores).length} of 12 questions answered
              </p>
            </div>
            <div className="text-center mb-8">
              <p className="public-eyebrow">Your strategy, as it works today</p>
              <h1 className="text-4xl md:text-5xl mb-4">{section.title}</h1>
              <p className="text-xl text-gray-600">{section.subtitle}</p>
            </div>
            <div className="space-y-6">
              {questions.map((question) => (
                <fieldset key={question.id} className="public-card public-card--white p-6 md:p-8">
                  <legend className="text-sm font-semibold text-gray-600 px-2">
                    Question {question.id} of 12
                  </legend>
                  <p className="text-lg font-medium mb-5">{question.text}</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {SCORE_LABELS.map((label) => (
                      <button
                        key={label.value}
                        type="button"
                        onClick={() => handleScoreChange(question.id, label.value)}
                        aria-pressed={scores[question.id] === label.value}
                        aria-label={`${label.value}: ${label.label}`}
                        className={`flex-1 px-3 py-3 rounded-xl border-2 transition-colors ${scores[question.id] === label.value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}
                      >
                        <span className="block text-2xl font-bold mb-1">{label.value}</span>
                        <span className="block text-xs text-gray-600">{label.label}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              {step > 1 && (
                <button onClick={handlePrev} className="public-button public-button--secondary">
                  <ArrowLeft className="h-5 w-5" />
                  Previous section
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!isCurrentSectionComplete()}
                className="public-button public-button--primary flex-1"
              >
                {step === 4 ? 'See my results' : 'Continue to the next section'}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  // Results step (5)
  if (step === 5) {
    const resultBand = getResultBand();
    const Icon = resultBand.icon;
    return (
      <MarketingLayout>
        <PageHero
          badge={<span>Your strategy health check</span>}
          title="Turn your results into a useful next step."
          subtitle="Your score reflects the answers you gave. Use it to focus a conversation, compare perspectives and choose what to improve first."
        />
        <section className="public-section">
          <div className="public-container max-w-4xl">
            <div className="public-card public-card--white p-8 md:p-10 mb-7">
              <div className="text-center mb-7">
                <p className="public-eyebrow">Your score</p>
                <p className="text-6xl font-bold">
                  {totalScore}
                  <span className="text-3xl text-gray-500">/{maxScore}</span>
                </p>
              </div>
              <Icon className={`h-10 w-10 mx-auto mb-5 ${resultBand.color}`} />
              <h2 className="text-center mb-4">{resultBand.title}</h2>
              <p className="text-xl text-gray-600 text-center mb-7">{resultBand.subtitle}</p>
              <div className="space-y-4">
                {resultBand.description.map((text) => (
                  <p key={text} className="text-gray-600">
                    {text}
                  </p>
                ))}
              </div>
            </div>
            <div className="public-card p-8 mb-7">
              <h3 className="mb-4">Choose one improvement you can act on.</h3>
              <p className="text-gray-600 mb-5">
                Look back at your lower-scoring areas and make the next move specific.
              </p>
              <ul className="grid md:grid-cols-2 gap-4">
                {[
                  'Clarify an objective and how you will measure it.',
                  'Connect one priority to a project or task.',
                  'Name the person accountable for the next step.',
                  'Agree when you will review progress.',
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center">
              <button onClick={handleNext} className="public-button public-button--primary">
                Get my detailed report <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  // Email capture step (6)
  if (step === 6) {
    return (
      <MarketingLayout>
        <PageHero
          badge={<span>Keep the insight. Choose your next move.</span>}
          title="Take your results into your next conversation."
          subtitle="Enter your details for an emailed health-check report and a sign-in link. We’ll create an account if you don’t already have one, so you can explore your next step in StratPlan."
        />
        <section className="public-section">
          <div className="public-container">
            <div className="public-card public-card--white p-8 md:p-10 max-w-xl mx-auto">
              <UserPlus className="h-9 w-9 text-blue-600 mb-5" />
              <h2 className="mb-6">Where should we send your report?</h2>
              {errorMessage && (
                <div
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
                  role="alert"
                >
                  {errorMessage}
                </div>
              )}
              <form onSubmit={handleSignUpSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border"
                    placeholder="Your full name"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="public-button public-button--primary w-full"
                >
                  {isSubmitting ? 'Preparing my report…' : 'Send my report and sign-in link'}
                  {!isSubmitting && <ArrowRight className="h-5 w-5" />}
                </button>
                <p className="text-sm text-gray-500 text-center">
                  By continuing, you agree to our Terms of Service and Privacy Policy. We’ll send
                  your report and a sign-in link by email. No password is needed for that link.
                </p>
              </form>
              <p className="mt-6 text-sm text-gray-600 text-center">
                Already have an account?{' '}
                <Link to="/sign-in" className="text-blue-600 font-semibold">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  // Email verification step (7)
  if (step === 7) {
    return (
      <MarketingLayout>
        <PageHero
          badge={<span>Your next step is in your inbox</span>}
          title="Keep the conversation moving."
          subtitle="Look out for your health-check report and sign-in link at the email address below. The link connects you to your StratPlan account."
        />
        <section className="public-section">
          <div className="public-container">
            <div className="public-card public-card--white p-8 md:p-10 max-w-xl mx-auto text-center">
              <CheckCircle className="h-12 w-12 text-blue-600 mx-auto mb-5" />
              <h2 className="mb-4">Check your inbox.</h2>
              <p className="text-xl font-semibold text-blue-600 mb-5">{formData.email}</p>
              <p className="text-gray-600 mb-5">
                Open the sign-in link to continue. Email can take a moment to arrive; check your
                spam folder if you don’t see it.
              </p>
              <p className="text-gray-600 mb-7">
                Use your report to choose a practical focus, then explore how your direction,
                priorities and work connect in StratPlan.
              </p>
              {errorMessage && (
                <p className="text-red-700 mb-5" role="alert">
                  {errorMessage}
                </p>
              )}
              <Link to="/sign-in" className="public-button public-button--secondary">
                Sign in with my email <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }
  return <MarketingLayout>{null}</MarketingLayout>;
}
